import React, { useEffect, useState } from "react";
import { Alert, Form, FormGroup, Row, Col, Badge, Button } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import queryString from "query-string";
import { ReduxState } from "../../../../../redux";
import { LoadingTrackedButton } from "../../../../Docuclipper/TrackedButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getIntegrations,
  getXeroBankAccounts,
  syncXeroBankTransactions,
  XeroBankAccount,
  XeroSyncResult,
} from "../../../../../docuclipper/api";
import { Integration } from "../../../../../docuclipper/DocuclipperTypes";
import { createAlert } from "../../../../../redux/reducers/Alerts";
import ClipLoader from "react-spinners/ClipLoader";
import { postNavigate } from "../../../../../utils/utils";

const XERO_SCOPE = "xero.accounting";

const INTEGRATION_XERO_URL = `${
  process.env.NODE_ENV === "production" ? "" : "http://localhost:8080"
}/api/v1/protected/auth/xero`;

/**
 * Navigate to Xero OAuth with a return URL that will reopen the download modal
 * with xero-sync selected and optionally preselect the new integration.
 * @param returnPath - The path to return to after OAuth (defaults to current path)
 * @param additionalParams - Additional query params to include in the return URL
 */
export const goToXeroWithReturn = (returnPath?: string, additionalParams?: Record<string, string>) => {
  // Build return URL with params to reopen modal
  const currentPath = returnPath || window.location.pathname;
  const returnParams = new URLSearchParams(window.location.search);
  returnParams.set("openDownload", "true");
  returnParams.set("exportType", "xero-sync");
  
  // Add any additional params (like job ID)
  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      returnParams.set(key, value);
    });
  }
  
  const returnUrl = `${currentPath}?${returnParams.toString()}`;
  const next = `&next=${encodeURIComponent(returnUrl)}`;
  postNavigate(`${INTEGRATION_XERO_URL}?integration=xero${next}`);
};

export const goToXero = () => {
  const next = `&next=${encodeURIComponent(`/integrations`)}`;
  postNavigate(`${INTEGRATION_XERO_URL}?integration=xero${next}`);
};

export const XeroSyncOptions: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [bankAccounts, setBankAccounts] = useState<XeroBankAccount[]>([]);
  const [bankAccountsLoading, setBankAccountsLoading] = useState(false);
  const [selectedIntegration, setSelectedIntegration] =
    useState<Integration | null>(null);
  const [selectedBankAccount, setSelectedBankAccount] =
    useState<XeroBankAccount | null>(null);
  const [syncResult, setSyncResult] = useState<XeroSyncResult | null>(null);

  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { downloadOptions } = useSelector(
    (state: ReduxState) => state.TransactionManager
  );
  const { selectedDocumentIds } = downloadOptions;
  
  // Get tag levels for cross-job queries
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );

  // Get preselected integration ID from URL params (set after OAuth redirect)
  const urlParams = queryString.parse(location.search);
  const preselectedIntegrationId = urlParams.xeroIntegrationId as string | undefined;

  // Fetch Xero integrations
  useEffect(() => {
    setIntegrationsLoading(true);
    getIntegrations()
      .then((allIntegrations) => {
        const xeroIntegrations = allIntegrations.filter(
          (i: Integration) => i.scope === XERO_SCOPE
        );
        setIntegrations(xeroIntegrations);
        
        // Auto-select integration: prefer preselected from URL, then single integration, then most recent
        if (xeroIntegrations.length > 0) {
          let integrationToSelect: Integration | null = null;
          
          if (preselectedIntegrationId) {
            // Try to find the preselected integration
            integrationToSelect = xeroIntegrations.find(
              (i: Integration) => i.id === preselectedIntegrationId
            ) || null;
          }
          
          if (!integrationToSelect && xeroIntegrations.length === 1) {
            integrationToSelect = xeroIntegrations[0];
          } else if (!integrationToSelect && xeroIntegrations.length > 1) {
            // Select the most recently added (last in the list, assuming sorted by creation)
            integrationToSelect = xeroIntegrations[xeroIntegrations.length - 1];
          }
          
          if (integrationToSelect) {
            setSelectedIntegration(integrationToSelect);
          }
        }
        
        setIntegrationsLoading(false);
      })
      .catch((err) => {
        setIntegrationsLoading(false);
        dispatch(
          createAlert({
            type: "error",
            message: err.message || "Error loading Xero integrations. Please try again.",
            timeout: 5000,
          })
        );
      });
  }, [dispatch, preselectedIntegrationId]);

  // Fetch bank accounts when integration is selected
  useEffect(() => {
    if (selectedIntegration) {
      setBankAccountsLoading(true);
      setBankAccounts([]);
      setSelectedBankAccount(null);
      getXeroBankAccounts(selectedIntegration.id)
        .then((accounts) => {
          setBankAccounts(accounts);
          if (accounts.length === 1) {
            setSelectedBankAccount(accounts[0]);
          }
          setBankAccountsLoading(false);
        })
        .catch((err) => {
          setBankAccountsLoading(false);
          dispatch(
            createAlert({
              type: "error",
              message: err.message || "Error loading Xero bank accounts. Please try again.",
              timeout: 5000,
            })
          );
        });
    }
  }, [selectedIntegration, dispatch]);

  const handleSync = async () => {
    // Validate prerequisites
    if (!selectedIntegration) {
      dispatch(
        createAlert({
          type: "error",
          message: "Please select a Xero account",
          timeout: 5000,
        })
      );
      return;
    }
    
    if (!selectedBankAccount) {
      dispatch(
        createAlert({
          type: "error",
          message: "Please select a destination bank account",
          timeout: 5000,
        })
      );
      return;
    }
    
    // Must have either l1/l2 OR job
    if (!l1 && !l2 && !job) {
      dispatch(
        createAlert({
          type: "error",
          message: "No transaction data available. Please make sure you have extracted transactions first.",
          timeout: 5000,
        })
      );
      return;
    }

    setSyncing(true);
    setSyncResult(null);

    try {
      // Use l1/l2 if available, otherwise use job.id
      const result = await syncXeroBankTransactions({
        integrationId: selectedIntegration.id,
        bankAccountId: selectedBankAccount.accountID,
        ...(l1 && l2 ? { l1, l2 } : { jobId: job?.id }),
        filters: [
          {
            field: "fieldName",
            operator: "equals",
            value: "bankMode",
          },
        ],
      });

      setSyncResult(result);
      dispatch(
        createAlert({
          type: result.totalErrors > 0 ? "error" : "success",
          message:
            result.totalErrors > 0
              ? `Synced ${result.totalSynced} transactions with ${result.totalErrors} errors`
              : `Successfully synced ${result.totalSynced} transactions to Xero`,
          timeout: 5000,
        })
      );
    } catch (err: any) {
      dispatch(
        createAlert({
          type: "error",
          message: err.message || "Error syncing to Xero. Please try again.",
          timeout: 5000,
        })
      );
    } finally {
      setSyncing(false);
    }
  };

  // Show loading state
  if (integrationsLoading) {
    return (
      <div className="text-center py-4">
        <ClipLoader size={30} />
        <p className="mt-2">Loading Xero integrations...</p>
      </div>
    );
  }

  // Handle connecting Xero - returns to this modal after OAuth
  const handleConnectXero = () => {
    // Pass job ID if available to preserve context
    const additionalParams: Record<string, string> = {};
    if (job?.id) {
      additionalParams.jobId = String(job.id);
    }
    goToXeroWithReturn(undefined, additionalParams);
  };

  // Show connect prompt if no integrations
  if (integrations.length === 0) {
    return (
      <Alert variant="warning">
        <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
        <strong>No Xero integration found.</strong>
        <p className="mt-2 mb-2">
          Connect your Xero account to sync bank transactions directly.
        </p>
        <LoadingTrackedButton
          variant="primary"
          onClick={handleConnectXero}
          isLoading={false}
          action="connectXeroAccount"
          label="xero-connect"
        >
          <FontAwesomeIcon icon="link" className="mr-2" />
          Connect Xero Account
        </LoadingTrackedButton>
      </Alert>
    );
  }

  const integrationOptions = integrations.map((i) => ({
    value: i.id,
    label: i.accountId || `Xero Account ${i.id}`,
  }));

  const bankAccountOptions = bankAccounts.map((a) => ({
    value: a.accountID,
    label: `${a.name} (${a.bankAccountNumber || a.code}) - ${a.currencyCode}`,
    account: a,
  }));

  return (
    <div className="xero-sync-options">
      <Alert variant="info" className="mb-3">
        <FontAwesomeIcon icon="info-circle" className="mr-2" />
        Sync your bank transactions directly to Xero without downloading files.
      </Alert>

      {/* Integration Selection */}
      <FormGroup className="mb-3">
        <Form.Label>
          <strong>Xero Account</strong>
          <Button
            variant="link"
            size="sm"
            className="ml-2 p-0"
            onClick={handleConnectXero}
            title="Connect another Xero organization"
          >
            <FontAwesomeIcon icon="plus" className="mr-1" />
            Add New
          </Button>
        </Form.Label>
        {integrations.length === 1 ? (
          <div className="p-2 bg-light rounded">
            <FontAwesomeIcon icon="check-circle" className="text-success mr-2" />
            {integrations[0].accountId || `Connected Account`}
          </div>
        ) : (
          <Select
            options={integrationOptions}
            value={
              selectedIntegration
                ? integrationOptions.find(
                    (o) => o.value === selectedIntegration.id
                  )
                : null
            }
            onChange={(option) => {
              const integration = integrations.find(
                (i) => i.id === option?.value
              );
              setSelectedIntegration(integration || null);
            }}
            placeholder="Select Xero account..."
            isLoading={integrationsLoading}
          />
        )}
      </FormGroup>

      {/* Bank Account Selection */}
      {selectedIntegration && (
        <FormGroup className="mb-3">
          <Form.Label>
            <strong>Destination Bank Account</strong>
          </Form.Label>
          {bankAccountsLoading ? (
            <div className="text-center py-2">
              <ClipLoader size={20} />
              <span className="ml-2">Loading bank accounts...</span>
            </div>
          ) : bankAccounts.length === 0 ? (
            <Alert variant="warning">
              <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
              No bank accounts found in Xero. Please add a bank account in Xero
              first.
            </Alert>
          ) : (
            <Select
              options={bankAccountOptions}
              value={
                selectedBankAccount
                  ? bankAccountOptions.find(
                      (o) => o.value === selectedBankAccount.accountID
                    )
                  : null
              }
              onChange={(option) => {
                setSelectedBankAccount(option?.account || null);
              }}
              placeholder="Select destination bank account..."
            />
          )}
        </FormGroup>
      )}

      {/* Selected Bank Account Details */}
      {selectedBankAccount && (
        <div className="mb-3 p-3 bg-light rounded">
          <Row>
            <Col md={6}>
              <small className="text-muted">Account Name</small>
              <div className="font-weight-bold">{selectedBankAccount.name}</div>
            </Col>
            <Col md={3}>
              <small className="text-muted">Currency</small>
              <div>
                <Badge variant="info">{selectedBankAccount.currencyCode}</Badge>
              </div>
            </Col>
            <Col md={3}>
              <small className="text-muted">Status</small>
              <div>
                <Badge
                  variant={
                    selectedBankAccount.status === "ACTIVE"
                      ? "success"
                      : "secondary"
                  }
                >
                  {selectedBankAccount.status}
                </Badge>
              </div>
            </Col>
          </Row>
        </div>
      )}

      {/* Sync Result */}
      {syncResult && (
        <Alert
          variant={syncResult.totalErrors > 0 ? "warning" : "success"}
          className="mb-3"
        >
          <h6>
            <FontAwesomeIcon
              icon={syncResult.totalErrors > 0 ? "exclamation-triangle" : "check-circle"}
              className="mr-2"
            />
            Sync Complete
          </h6>
          <Row className="mt-2">
            <Col>
              <small className="text-muted">Found</small>
              <div className="font-weight-bold">{syncResult.totalFound}</div>
            </Col>
            <Col>
              <small className="text-muted">Synced</small>
              <div className="font-weight-bold text-success">
                {syncResult.totalSynced}
              </div>
            </Col>
            <Col>
              <small className="text-muted">Already Synced</small>
              <div className="font-weight-bold text-info">
                {syncResult.alreadySynced}
              </div>
            </Col>
            <Col>
              <small className="text-muted">Errors</small>
              <div
                className={`font-weight-bold ${
                  syncResult.totalErrors > 0 ? "text-danger" : ""
                }`}
              >
                {syncResult.totalErrors}
              </div>
            </Col>
          </Row>
          {syncResult.errors && syncResult.errors.length > 0 && (
            <div className="mt-2">
              <small className="text-muted">Error Details:</small>
              {syncResult.errors.map((err, idx) => (
                <div key={idx} className="text-danger small">
                  Batch {err.batch}: {err.error}
                </div>
              ))}
            </div>
          )}
        </Alert>
      )}

      {/* Validation Messages */}
      {!l1 && !l2 && !job && (
        <Alert variant="warning" className="mb-3">
          <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
          No transaction data available. Please make sure you have extracted transactions first.
        </Alert>
      )}

      {/* Sync Button */}
      <Row className="justify-content-end mx-0">
        <LoadingTrackedButton
          isLoading={syncing}
          className="btn-sync-xero"
          variant="primary"
          disabled={!selectedIntegration || !selectedBankAccount || (!l1 && !l2 && !job)}
          onClick={handleSync}
          action="syncToXero"
          label="xero-sync"
        >
          <FontAwesomeIcon icon="sync" className="mr-2" />
          Sync to Xero
        </LoadingTrackedButton>
      </Row>
    </div>
  );
};

export default XeroSyncOptions;

